import { OptionCard } from "@/components/WizardShell";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";

function YesNoRow({ label, value, onChange, testId, options }) {
  const { t } = useTranslation();
  const opts = options || [
    { id: "yes", label: t("wizard.yesno.yes") },
    { id: "partial", label: t("wizard.yesno.partial") },
    { id: "no", label: t("wizard.yesno.no") },
    { id: "unknown", label: t("wizard.yesno.unknown") },
  ];
  return (
    <div className="py-4 border-b border-slate-100 last:border-0">
      <div className="text-slate-800 font-medium">{label}</div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {opts.map((o) => (
          <button key={o.id} type="button" onClick={() => onChange(o.id)}
            className={`rounded-sm border px-3 py-2 text-sm transition-colors ${
              value === o.id ? "border-brand-fuchsia bg-fuchsia-50/40 text-brand-bordeaux font-semibold" : "border-slate-200 text-slate-700 hover:border-brand-fuchsia"
            }`}
            data-testid={`${testId}-${o.id}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, testId, min = 0 }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <Input type="number" min={min} value={value} onChange={(e) => onChange(parseInt(e.target.value || "0", 10))} className="mt-2 max-w-[220px]" data-testid={testId} />
    </div>
  );
}

export default function Step3Architecture({ ref_data, value, onChange }) {
  const { t } = useTranslation();
  const set = (patch) => onChange(patch);
  return (
    <div className="space-y-10 py-8" data-testid="step-3">
      <div className="grid sm:grid-cols-3 gap-6">
        <NumberField label={t("wizard.s3_forests")} value={value.forests} onChange={(v) => set({ forests: v })} testId="arch-forests" min={1} />
        <NumberField label={t("wizard.s3_domains")} value={value.domains} onChange={(v) => set({ domains: v })} testId="arch-domains" min={1} />
        <NumberField label={t("wizard.s3_dcs")} value={value.dcs} onChange={(v) => set({ dcs: v })} testId="arch-dcs" min={1} />
      </div>

      <div>
        <div className="label-caps">{t("wizard.s3_deploy")}</div>
        <div className="mt-3 grid sm:grid-cols-3 gap-3">
          {ref_data.deployment.map((d) => (
            <OptionCard key={d.id} selected={value.deployment === d.id} onClick={() => set({ deployment: d.id })} testId={`arch-deploy-${d.id}`}>
              {d.label}
            </OptionCard>
          ))}
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="label-caps">{t("wizard.s3_add")}</div>
        <div className="mt-2 divide-y divide-slate-100">
          <YesNoRow label={t("wizard.s3_multi")} value={value.multi_entity} onChange={(v) => set({ multi_entity: v })} testId="arch-multi" />
          <YesNoRow label={t("wizard.s3_trusts")} value={value.trusts} onChange={(v) => set({ trusts: v })} testId="arch-trusts" />
          <YesNoRow label={t("wizard.s3_adfs")} value={value.adfs} onChange={(v) => set({ adfs: v })} testId="arch-adfs" />
          <YesNoRow label={t("wizard.s3_adcs")} value={value.adcs} onChange={(v) => set({ adcs: v })} testId="arch-adcs" />
          <YesNoRow label={t("wizard.s3_entra")} value={value.entra_connect} onChange={(v) => set({ entra_connect: v })} testId="arch-entra" />
          <YesNoRow label={t("wizard.s3_sep")} value={value.separate_admin_accounts} onChange={(v) => set({ separate_admin_accounts: v })} testId="arch-sep-admin" />
          <YesNoRow label={t("wizard.s3_test")} value={value.test_env} onChange={(v) => set({ test_env: v })} testId="arch-test" />
          <YesNoRow label={t("wizard.s3_retention")} value={value.log_retention} onChange={(v) => set({ log_retention: v })} testId="arch-retention" />
          <YesNoRow label={t("wizard.s3_advaudit")} value={value.advanced_audit} onChange={(v) => set({ advanced_audit: v })} testId="arch-advaudit" />
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-6">
        <NumberField label={t("wizard.s3_priv")} value={value.privileged_accounts} onChange={(v) => set({ privileged_accounts: v })} testId="arch-priv" />
        <NumberField label={t("wizard.s3_svc")} value={value.service_accounts} onChange={(v) => set({ service_accounts: v })} testId="arch-svc" />
        <NumberField label={t("wizard.s3_ext")} value={value.external_admins} onChange={(v) => set({ external_admins: v })} testId="arch-ext" />
      </div>
    </div>
  );
}

export const step3Valid = (v) => v.forests > 0 && v.domains > 0 && v.dcs > 0 && !!v.deployment;
